import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  PhotoIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const toMoney = (value) => {
  const number = Number(value);
  return `$${Number.isFinite(number) ? number.toFixed(2) : '0.00'}`;
};

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');

const getFileUrl = (value) => {
  if (!value) return '';
  const raw = String(value);
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return raw;
  }
  return `/${raw}`;
};

const getFileKind = (value) => {
  const clean = String(value || '').split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|webp|gif)$/i.test(clean)) return 'image';
  if (/\.pdf$/i.test(clean)) return 'pdf';
  return 'document';
};

const getFileName = (value) => {
  const clean = String(value || '').split('?')[0];
  const pieces = clean.split('/').filter(Boolean);
  return pieces[pieces.length - 1] || 'comprobante';
};

const formatPeriod = (payment) => {
  if (!payment?.periodo_year || !payment?.periodo_month) return '-';
  return `${payment.periodo_year}-${String(payment.periodo_month).padStart(2, '0')}`;
};

const formatCompanies = (payment) => (payment?.companies || []).join(', ') || '-';

const CreditPaymentApprovals = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [matrizadores, setMatrizadores] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [activePaymentId, setActivePaymentId] = useState(null);
  const [filters, setFilters] = useState({ fecha_documento: '', usuario_matrizador: '' });
  const [accounting, setAccounting] = useState({
    period: 'month',
    year: String(new Date().getFullYear()),
    month: String(new Date().getMonth() + 1),
    date_from: '',
    date_to: '',
  });
  const [isExportingAccounting, setIsExportingAccounting] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(String(row.id))),
    [rows, selectedIds]
  );

  const selectedTotal = useMemo(
    () => selectedRows.reduce((acc, row) => acc + (Number(row.monto_total) || 0), 0),
    [selectedRows]
  );

  const activePayment = useMemo(() => {
    if (!rows.length) return null;
    return rows.find((row) => String(row.id) === String(activePaymentId)) || null;
  }, [activePaymentId, rows]);

  const activeFileUrl = getFileUrl(activePayment?.archivo);
  const activeFileKind = getFileKind(activeFileUrl);

  const fetchCatalogs = useCallback(async () => {
    const response = await api.get('/api/audit/matrizadores/list/');
    setMatrizadores(Array.isArray(response.data) ? response.data : []);
  }, []);

  const fetchRows = useCallback(async () => {
    setLoadingData(true);
    try {
      const params = {};
      if (filters.fecha_documento) params.fecha_documento = filters.fecha_documento;
      if (filters.usuario_matrizador) params.usuario_matrizador = filters.usuario_matrizador;
      const response = await api.get('/api/audit/credit-payments/approve/', { params });
      const nextRows = Array.isArray(response.data?.results) ? response.data.results : [];
      setRows(nextRows);
      setSummary(response.data?.summary || null);
      setSelectedIds(new Set());
      setActivePaymentId(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudieron cargar los comprobantes de crédito.');
      setRows([]);
      setSummary(null);
      setActivePaymentId(null);
    } finally {
      setLoadingData(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!loading && user?.is_notario) {
      fetchCatalogs().catch(() => toast.error('No se pudo cargar la lista de matrizadores.'));
      fetchRows();
    }
  }, [loading, user, fetchCatalogs, fetchRows]);

  const openPaymentProof = (payment) => {
    setActivePaymentId(payment.id);
    setSelectedIds(new Set([String(payment.id)]));
  };

  const handleAccountingExport = async () => {
    const params = { period: accounting.period };
    if (accounting.period === 'range') {
      if (!accounting.date_from || !accounting.date_to || accounting.date_from > accounting.date_to) {
        toast.error('Selecciona un rango de fechas válido.', { position: 'top-right', autoClose: 4000 });
        return;
      }
      params.date_from = accounting.date_from;
      params.date_to = accounting.date_to;
    } else if (accounting.period === 'year') {
      params.year = accounting.year;
    } else {
      params.year = accounting.year;
      params.month = accounting.month;
    }

    setIsExportingAccounting(true);
    try {
      const response = await api.get('/api/audit/reports/approved-credit-payments-accounting/', {
        params,
        responseType: 'blob',
      });
      const disposition = response?.headers?.['content-disposition']
        || response?.headers?.get?.('content-disposition');
      const match = disposition && /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match ? match[1] : `informe-contable-creditos-${params.year || ''}.xlsx`;
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success('Informe contable generado.', { position: 'top-right', autoClose: 3000 });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'No se pudo generar el informe contable.', {
        position: 'top-right', autoClose: 5000,
      });
    } finally {
      setIsExportingAccounting(false);
    }
  };

  const toggleSelected = (payment) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(payment.id);
      if (next.has(key)) {
        next.delete(key);
        if (String(activePaymentId) === key) {
          const nextActivePayment = rows.find((row) => next.has(String(row.id)));
          setActivePaymentId(nextActivePayment?.id || null);
        }
      } else {
        next.add(key);
        setActivePaymentId(payment.id);
      }
      return next;
    });
  };

  const selectionLabel = selectedRows.length === 1
    ? `comprobante #${selectedRows[0].id}`
    : `${selectedRows.length} comprobantes`;

  const submitReview = async () => {
    if (!reviewAction || selectedRows.length === 0) return;
    setSubmitting(true);
    try {
      await api.patch('/api/audit/credit-payments/approve/', {
        ids: selectedRows.map((row) => row.id),
        action: reviewAction,
        notes: reviewNotes,
      });
      toast.success(reviewAction === 'aprobar' ? 'Comprobante de crédito aprobado.' : 'Comprobante de crédito rechazado.');
      setReviewAction(null);
      setReviewNotes('');
      await fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo revisar la selección.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-white text-center">Cargando...</div>;
  if (!user) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-[#081426] p-4 text-white sm:p-6">
      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />

      <header className="mx-auto mb-6 flex max-w-[1500px] flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Notaría 4 Cantón Otavalo" className="w-12 sm:w-14" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-notaria-yellow">Control notarial de créditos</p>
            <h1 className="text-2xl font-extrabold leading-tight sm:text-3xl">Aprobar trámites a crédito</h1>
            <p className="max-w-2xl text-sm text-slate-300">Revise el comprobante en pantalla, valide el total y apruebe o rechace sin abrir ventanas externas.</p>
          </div>
        </div>
        <Link to="/dashboard/notario" className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 transition hover:bg-notaria-red hover:text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-[#081426]">
          Volver
        </Link>
      </header>

      <main className="mx-auto max-w-[1500px] space-y-5">
        <section className="rounded-xl border border-white/10 bg-white/[0.04] p-4 shadow-2xl shadow-black/30 backdrop-blur">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(180px,220px)_minmax(220px,1fr)_180px_56px]">
            <input
              type="date"
              value={filters.fecha_documento}
              onChange={(e) => setFilters((prev) => ({ ...prev, fecha_documento: e.target.value }))}
              className="rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none transition focus:border-notaria-yellow focus:ring-2 focus:ring-notaria-yellow/30"
              aria-label="Filtrar por día"
            />
            <select
              value={filters.usuario_matrizador}
              onChange={(e) => setFilters((prev) => ({ ...prev, usuario_matrizador: e.target.value }))}
              className="rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none transition focus:border-notaria-yellow focus:ring-2 focus:ring-notaria-yellow/30"
              aria-label="Filtrar por matrizador"
            >
              <option value="">Todos los matrizadores</option>
              {matrizadores.map((matrizador) => (
                <option key={matrizador.nombre} value={normalizeText(matrizador.nombre)}>
                  {matrizador.nombre}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={fetchRows}
              className="rounded-lg bg-notaria-yellow px-4 py-3 font-bold text-notaria-blue-1 transition hover:-translate-y-0.5 hover:bg-notaria-red hover:text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-[#081426] active:translate-y-0"
            >
              Aplicar filtros
            </button>
            <button
              type="button"
              onClick={fetchRows}
              className="flex h-full min-h-[48px] items-center justify-center rounded-lg border border-white/10 bg-[#102447] px-4 font-semibold transition hover:bg-[#173263] focus:outline-none focus:ring-2 focus:ring-notaria-yellow focus:ring-offset-2 focus:ring-offset-[#081426]"
              aria-label="Recargar comprobantes"
            >
              <ArrowPathIcon className={`h-5 w-5 ${loadingData ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <p className="mb-3 text-sm font-semibold text-slate-200">
            Informe contable de pagos de crédito aprobados
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <select
              value={accounting.period}
              onChange={(e) => setAccounting((prev) => ({ ...prev, period: e.target.value }))}
              className="rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none transition focus:border-notaria-yellow"
              aria-label="Periodo del informe contable"
            >
              <option value="month">Mes</option>
              <option value="year">Año</option>
              <option value="range">Rango de fechas</option>
            </select>

            {accounting.period === 'month' && (
              <>
                <input
                  type="number"
                  value={accounting.year}
                  onChange={(e) => setAccounting((prev) => ({ ...prev, year: e.target.value }))}
                  className="w-28 rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none"
                  aria-label="Año"
                />
                <select
                  value={accounting.month}
                  onChange={(e) => setAccounting((prev) => ({ ...prev, month: e.target.value }))}
                  className="rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none"
                  aria-label="Mes"
                >
                  {['1 Enero', '2 Febrero', '3 Marzo', '4 Abril', '5 Mayo', '6 Junio',
                    '7 Julio', '8 Agosto', '9 Septiembre', '10 Octubre', '11 Noviembre', '12 Diciembre']
                    .map((label) => {
                      const value = String(label.split(' ')[0]);
                      return <option key={value} value={value}>{label}</option>;
                    })}
                </select>
              </>
            )}

            {accounting.period === 'year' && (
              <input
                type="number"
                value={accounting.year}
                onChange={(e) => setAccounting((prev) => ({ ...prev, year: e.target.value }))}
                className="w-32 rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none"
                aria-label="Año"
              />
            )}

            {accounting.period === 'range' && (
              <>
                <input
                  type="date"
                  value={accounting.date_from}
                  onChange={(e) => setAccounting((prev) => ({ ...prev, date_from: e.target.value }))}
                  className="rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none"
                  aria-label="Desde"
                />
                <input
                  type="date"
                  value={accounting.date_to}
                  min={accounting.date_from || undefined}
                  onChange={(e) => setAccounting((prev) => ({ ...prev, date_to: e.target.value }))}
                  className="rounded-lg border border-white/10 bg-[#102447] p-3 text-white outline-none"
                  aria-label="Hasta"
                />
              </>
            )}

            <button
              type="button"
              onClick={handleAccountingExport}
              disabled={isExportingAccounting}
              className="flex items-center gap-2 rounded-lg bg-notaria-blue-3 px-4 py-3 font-bold text-white transition hover:bg-notaria-blue-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isExportingAccounting ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
              ) : (
                <DocumentTextIcon className="h-5 w-5" />
              )}
              {isExportingAccounting ? 'Generando...' : 'Informe contable XLSX'}
            </button>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-[#0c1b32] p-4 shadow-lg shadow-black/20">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-300">Comprobantes pendientes</p>
              <DocumentTextIcon className="h-5 w-5 text-notaria-yellow" />
            </div>
            <p className="mt-2 text-3xl font-extrabold tabular-nums">{summary?.count || 0}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#0c1b32] p-4 shadow-lg shadow-black/20">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-300">Total pendiente</p>
              <BanknotesIcon className="h-5 w-5 text-notaria-yellow" />
            </div>
            <p className="mt-2 text-3xl font-extrabold tabular-nums">{toMoney(summary?.total)}</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#0c1b32] p-4 shadow-lg shadow-black/20">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-slate-300">Seleccionado</p>
              <CheckCircleIcon className="h-5 w-5 text-notaria-yellow" />
            </div>
            <p className="mt-2 text-3xl font-extrabold tabular-nums">{selectedRows.length} / {toMoney(selectedTotal)}</p>
          </div>
        </section>

        {selectedRows.length > 0 && (
          <section className="rounded-xl border border-notaria-yellow/40 bg-notaria-yellow/10 p-4 shadow-xl shadow-black/25">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase text-notaria-yellow">Alcance de la acción</p>
                <p className="font-semibold">
                  {selectedRows.map((row) => `#${row.id}`).join(', ')} por {toMoney(selectedTotal)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setReviewAction('aprobar')} className="rounded-lg bg-emerald-600 px-4 py-2 font-bold transition hover:-translate-y-0.5 hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-300 active:translate-y-0">
                  Aprobar {selectionLabel}
                </button>
                <button onClick={() => setReviewAction('rechazar')} className="rounded-lg bg-notaria-red px-4 py-2 font-bold transition hover:-translate-y-0.5 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-300 active:translate-y-0">
                  Rechazar {selectionLabel}
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(430px,0.95fr)_minmax(560px,1.35fr)]">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 shadow-2xl shadow-black/30">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">Comprobantes para revisar</h2>
                <p className="text-sm text-slate-300">Seleccione uno para ver el soporte aquí mismo.</p>
              </div>
              <span className="rounded-md bg-[#102447] px-3 py-1 text-sm font-semibold text-notaria-yellow">{rows.length} visibles</span>
            </div>

            <div className="max-h-[72vh] space-y-3 overflow-y-auto pr-1">
              {loadingData ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((item) => (
                    <div key={item} className="h-32 animate-pulse rounded-xl bg-white/10" />
                  ))}
                </div>
              ) : rows.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/15 bg-[#0c1b32] p-8 text-center text-slate-300">
                  No hay comprobantes de crédito pendientes con los filtros actuales.
                </div>
              ) : rows.map((row) => {
                const isActive = String(activePayment?.id) === String(row.id);
                const isSelected = selectedIds.has(String(row.id));
                return (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => openPaymentProof(row)}
                    aria-label={`Abrir comprobante #${row.id}`}
                    className={`w-full rounded-xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-notaria-yellow ${
                      isActive
                        ? 'border-notaria-yellow bg-notaria-yellow/10 shadow-lg shadow-notaria-yellow/10'
                        : 'border-white/10 bg-[#0c1b32] hover:border-white/25 hover:bg-[#102447]'
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-[#132b54] px-2 py-1 text-xs font-bold text-notaria-yellow">#{row.id}</span>
                          <span className="text-xs text-slate-300">{row.tipo_pago || 'comprobante'}</span>
                        </div>
                        <p className="mt-2 text-lg font-extrabold tabular-nums">{toMoney(row.monto_total)}</p>
                        <p className="text-xs text-slate-300">{row.numero_transaccion || 'Sin número de transacción'}</p>
                      </div>
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm font-semibold" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelected(row)}
                          aria-label={`Seleccionar comprobante #${row.id}`}
                          className="h-5 w-5 rounded text-notaria-yellow"
                        />
                        Seleccionar
                      </label>
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-200 sm:grid-cols-2">
                      <span className="flex items-start gap-2">
                        <DocumentTextIcon className="mt-0.5 h-4 w-4 shrink-0 text-notaria-yellow" />
                        {(row.companies || []).join(', ') || '-'}
                      </span>
                      <span className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-4 w-4 text-notaria-yellow" />
                        Período {formatPeriod(row)}
                      </span>
                      <span className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-4 w-4 text-notaria-yellow" />
                        {(row.dates || []).join(', ') || '-'}
                      </span>
                      <span className="flex items-start gap-2">
                        <UserGroupIcon className="mt-0.5 h-4 w-4 shrink-0 text-notaria-yellow" />
                        {(row.matrizadores || []).join(', ') || '-'}
                      </span>
                      <span className="flex items-center gap-2">
                        <BanknotesIcon className="h-4 w-4 text-notaria-yellow" />
                        {row.receivables_count} créditos
                      </span>
                      <span className="flex items-center gap-2">
                        <EyeIcon className="h-4 w-4 text-notaria-yellow" />
                        Ver soporte
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <aside className="rounded-xl border border-white/10 bg-[#07101f] p-4 shadow-2xl shadow-black/40 xl:sticky xl:top-4 xl:self-start">
            {activePayment ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-notaria-yellow">Visualizador de comprobante</p>
                    <h2 className="mt-1 text-2xl font-extrabold">Comprobante #{activePayment.id}</h2>
                    <p className="text-sm text-slate-300">{getFileName(activePayment.archivo)}</p>
                    <p className="mt-2 text-xs font-semibold text-emerald-300">
                      Incluido en el alcance: {selectionLabel}
                    </p>
                  </div>
                  {activeFileUrl && (
                    <a
                      href={activeFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-bold text-notaria-yellow transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                    >
                      Abrir
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-lg bg-white/[0.04] p-3">
                    <p className="text-xs text-slate-400">Total</p>
                    <p className="font-extrabold tabular-nums text-notaria-yellow">{toMoney(activePayment.monto_total)}</p>
                  </div>
                  <div className="rounded-lg bg-white/[0.04] p-3">
                    <p className="text-xs text-slate-400">Créditos</p>
                    <p className="font-extrabold tabular-nums">{activePayment.receivables_count}</p>
                  </div>
                  <div className="rounded-lg bg-white/[0.04] p-3">
                    <p className="text-xs text-slate-400">Período contable</p>
                    <p className="font-extrabold tabular-nums">{formatPeriod(activePayment)}</p>
                  </div>
                  <div className="rounded-lg bg-white/[0.04] p-3">
                    <p className="text-xs text-slate-400">Fecha de pago</p>
                    <p className="font-extrabold tabular-nums">{activePayment.fecha_pago || '-'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 rounded-lg bg-white/[0.04] p-3 text-sm text-slate-200 md:grid-cols-2">
                  <p><span className="text-slate-400">Empresa:</span> {(activePayment.companies || []).join(', ') || '-'}</p>
                  <p><span className="text-slate-400">Período:</span> {formatPeriod(activePayment)}</p>
                  <p><span className="text-slate-400">Días de trámite:</span> {(activePayment.dates || []).join(', ') || '-'}</p>
                  <p><span className="text-slate-400">Matrizadores:</span> {(activePayment.matrizadores || []).join(', ') || '-'}</p>
                  <p><span className="text-slate-400">Responsables:</span> {(activePayment.responsibles || []).join(', ') || '-'}</p>
                  <p><span className="text-slate-400">Transacción:</span> {activePayment.numero_transaccion || '-'}</p>
                  <p><span className="text-slate-400">Registrado por:</span> {activePayment.registrado_por || '-'}</p>
                </div>

                <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0c1b32]">
                  <div className="flex items-center justify-between border-b border-white/10 bg-white/[0.03] px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-bold">
                      {activeFileKind === 'image' ? <PhotoIcon className="h-5 w-5 text-notaria-yellow" /> : <DocumentTextIcon className="h-5 w-5 text-notaria-yellow" />}
                      Soporte adjunto
                    </div>
                    <span className="rounded bg-black/20 px-2 py-1 text-xs uppercase text-slate-300">{activeFileKind}</span>
                  </div>
                  <div className="h-[68vh] min-h-[520px] bg-[#050b15]">
                    {!activeFileUrl ? (
                      <div className="flex h-full items-center justify-center p-6 text-center text-slate-300">
                        Este comprobante no tiene archivo adjunto.
                      </div>
                    ) : activeFileKind === 'image' ? (
                      <div className="flex h-full items-center justify-center overflow-auto p-4">
                        <img src={activeFileUrl} alt={`Comprobante de crédito ${activePayment.id}`} className="max-h-full max-w-full rounded-lg object-contain shadow-2xl" />
                      </div>
                    ) : (
                      <iframe
                        title={`Comprobante de crédito ${activePayment.id}`}
                        src={activeFileUrl}
                        className="h-full w-full border-0"
                      />
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                  <h3 className="mb-3 font-bold">Créditos incluidos</h3>
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {(activePayment.receivables || []).map((credit) => (
                      <div key={credit.id} className="rounded-lg bg-[#0c1b32] p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-bold">{credit.tramite?.no_temporal || `Crédito ${credit.id}`}</p>
                          <p className="font-extrabold tabular-nums text-notaria-yellow">{toMoney(credit.monto)}</p>
                        </div>
                        <p className="text-slate-300">{credit.tramite?.acto || '-'}</p>
                        <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-slate-300 sm:grid-cols-2">
                          <p><span className="text-slate-500">Empresa:</span> {credit.company?.nombre || '-'}</p>
                          <p><span className="text-slate-500">Responsable:</span> {credit.responsible?.nombre || '-'}</p>
                          <p><span className="text-slate-500">Día:</span> {credit.fecha_tramite || '-'}</p>
                          <p><span className="text-slate-500">Matrizador:</span> {credit.usuario_matrizador || '-'}</p>
                          <p><span className="text-slate-500">No. comprobante:</span> {credit.tramite?.no_comprobante || '-'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[520px] items-center justify-center rounded-xl border border-dashed border-white/15 bg-[#0c1b32] p-8 text-center text-slate-300">
                Seleccione un comprobante pendiente para visualizar el soporte.
              </div>
            )}
          </aside>
        </section>
      </main>

      <AnimatePresence>
        {reviewAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="credit-payment-review-title"
          >
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="w-full max-w-lg rounded-xl border border-white/10 bg-notaria-blue-1 p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 id="credit-payment-review-title" className="text-xl font-bold">
                  {reviewAction === 'aprobar' ? `Aprobar ${selectionLabel}` : `Rechazar ${selectionLabel}`}
                </h2>
                <button onClick={() => setReviewAction(null)} className="text-gray-300 hover:text-notaria-yellow" aria-label="Cerrar revisión">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-notaria-yellow">Alcance de la acción</p>
                  <div className="max-h-52 space-y-2 overflow-y-auto">
                    {selectedRows.map((row) => (
                      <div key={row.id} className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm">
                        <p className="font-bold">Comprobante #{row.id}</p>
                        <p className="text-slate-300">Empresa: {formatCompanies(row)}</p>
                        <p className="text-slate-300">Período: {formatPeriod(row)}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 font-semibold">Total seleccionado: {toMoney(selectedTotal)}</p>
                </div>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  aria-label="Notas de revisión"
                  className="w-full rounded-lg bg-notaria-blue-2 p-2 text-white outline-none focus:ring-2 focus:ring-notaria-yellow"
                  placeholder="Notas"
                />
                <button
                  onClick={submitReview}
                  disabled={submitting}
                  className={`w-full rounded-lg bg-notaria-yellow py-2 font-bold text-notaria-blue-1 transition hover:bg-notaria-red hover:text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow ${submitting ? 'opacity-50' : ''}`}
                >
                  {submitting ? <ArrowPathIcon className="mr-2 inline h-5 w-5 animate-spin" /> : <CheckCircleIcon className="mr-2 inline h-5 w-5" />}
                  Confirmar {reviewAction === 'aprobar' ? 'aprobación' : 'rechazo'} de {selectionLabel}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CreditPaymentApprovals;
